import { useState, useEffect, useRef } from "react";
import { useTranslation as useTranslationVerseOverlay } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Highlighter, Bookmark, StickyNote, Sparkles, BookOpenCheck, Share2, X, Trash2, Save, Image as ImageIcon, Loader2, GraduationCap, Users, ShoppingBasket, Check, ChevronDown, MessageCircle } from "lucide-react";
import { api, stripNikud } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useBasket } from "../context/BasketContext";
import { openChavrutaWithContext } from "./chavruta/ChavrutaBubble";
import { useLearning } from "../context/LearningContext";
import { toast } from "sonner";
import AIAssistant from "./AIAssistant";
import CommentaryTabs from "./CommentaryTabs";
import CommentaryCompare from "./CommentaryCompare";
import ShareVerseCard from "./ShareVerseCard";
import LessonBuilder from "./LessonBuilder";
import WhoSaysWhat from "./WhoSaysWhat";
import SaveToNotebookButton from "./SaveToNotebookButton";
import { useLanguage } from "../context/LanguageContext";
import useRequireAuth from "../hooks/useRequireAuth";
import { requireAuth } from "../lib/authGate";
import { numberToHebrewLetter } from "../lib/hebrewNumerals";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Highlight color → semantic-reason mapping (per spec):
//   yellow=חשוב · blue=שאלה · green=רעיון לשיעור · red=צריך עיון · purple=השראה · gold=מקור מרכזי
// Name is resolved at render via `t()` so EN mode shows English labels;
// HE mode is byte-identical to the legacy hardcoded strings.
const HL_COLORS = [
  { key: "gold",   nameKey: "verse_overlay.highlight_central_source", reason: "source",    cls: "bg-amber-400" },
  { key: "yellow", nameKey: "verse_overlay.highlight_important",      reason: "important", cls: "bg-yellow-300" },
  { key: "blue",   nameKey: "verse_overlay.highlight_question",       reason: "question",  cls: "bg-blue-400" },
  { key: "green",  nameKey: "verse_overlay.highlight_lesson_idea",    reason: "lesson",    cls: "bg-emerald-500" },
  { key: "red",    nameKey: "verse_overlay.highlight_needs_study",    reason: "study",     cls: "bg-rose-500" },
  { key: "purple", nameKey: "verse_overlay.highlight_inspiration",    reason: "inspire",   cls: "bg-purple-500" },
];

// Simple Commentary Card component
function CommentaryCard({ commentator, commentatorHe, textHe, textEn }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="rounded-md border border-border/40 bg-card/30 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-right hover:bg-accent/30 transition-colors"
      >
        <div className="flex flex-col items-start">
          <span className="font-heading text-base text-foreground">{commentatorHe}</span>
          <span className="text-xs text-muted-foreground font-latin">{commentator}</span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} 
          strokeWidth={1.5} 
        />
      </button>
      
      {expanded && (textHe || textEn) && (
        <div className="px-4 py-4 bg-background/50 border-t border-border/30">
          {textHe && (
            <p 
              dir="rtl" 
              className="text-base leading-relaxed text-foreground text-right mb-2"
              style={{ unicodeBidi: "embed" }}
            >
              {textHe}
            </p>
          )}
          {textEn && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {textEn}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function VerseOverlay({
  open,
  onClose,
  book,
  chapter,
  verse,
  bookHe,
  bookEn,
  existingNote,
  existingHighlight,
  onUserDataChange,
  presentation = "sheet", // "sheet" (mobile/tablet) | "panel" (desktop inline)
}) {
  const { user } = useAuth();
  const { nikud, theme } = useTheme();
  const basket = useBasket();
  const learning = useLearning();
  const { isEn } = useLanguage();
  // Local translator. Default RTL (HE) — when isEn is true we switch
  // every label / toast / aria via t("verse_overlay.*"). HE mode is
  // byte-identical to the legacy hardcoded strings.
  const { t } = useTranslationVerseOverlay();
  // Auth gate for sign-up-required actions (Build Lesson / Who Says What).
  // `gate(fn, { feature })` wraps a click handler so anonymous users see
  // the contextual sign-up dialog instead of triggering the protected
  // flow. `RequireAuthModal` must be rendered inside this component.
  const { gate, RequireAuthModal } = useRequireAuth();
  const [tab, setTab] = useState("ai"); // Start with AI commentary
  const [commentaries, setCommentaries] = useState([]);
  const [loadingComm, setLoadingComm] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showLessonBuilder, setShowLessonBuilder] = useState(false);
  const [showWhoSaysWhat, setShowWhoSaysWhat] = useState(false);
  // Switch the verse-overlay presentation between an iOS-style bottom
  // sheet (mobile, more native feel) and a side drawer (desktop). Tracked
  // here at the top of the component so the hook ordering is stable.
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const shareCardRef = useRef(null);

  // Live highlight & note for THIS verse — derived from the global learning
  // store so any update from any screen is reflected here immediately.
  const liveHighlight = verse
    ? learning.getHighlight(book, chapter, verse.verse)
    : null;
  const liveNote = verse
    ? learning.getNote(book, chapter, verse.verse)
    : null;
  // Props are deprecated but still honored as a fallback for callers that
  // haven't been migrated.
  const effectiveHighlight = liveHighlight || existingHighlight;
  const effectiveNote = liveNote || existingNote;

  useEffect(() => {
    if (open && verse) {
      setCommentaries([]);
      setLoadingComm(true);
      api
        .commentaries(book, chapter, verse.verse)
        .then((res) => setCommentaries(res.commentaries || []))
        .catch(() => {})
        .finally(() => setLoadingComm(false));
      setNoteText(effectiveNote?.text || "");
      setTab("commentary");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, verse, book, chapter]);

  // Keep the textarea synced if a note is created/edited elsewhere
  useEffect(() => {
    if (open && verse) {
      setNoteText(effectiveNote?.text || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveNote?.id, effectiveNote?.text]);

  const handleHighlight = async (color, reason) => {
    if (!requireAuth("highlight")) return;
    if (!user) return;
    try {
      // Use the global LearningContext so the change appears EVERYWHERE
      // instantly: the 3D book repaints, the highlights index updates,
      // basket lookups stay accurate.
      const live = learning.getHighlight(book, chapter, verse.verse);
      if (live && live.color === color) {
        await learning.removeHighlight(live.id);
        toast.success(t("verse_overlay.toast_highlight_removed"));
      } else {
        await learning.upsertHighlight({
          book,
          chapter,
          verse: verse.verse,
          color,
          reason,
        });
        toast.success(t("verse_overlay.toast_highlight_added"));
      }
    } catch (e) {
      toast.error(t("verse_overlay.toast_highlight_error"));
    }
  };

  const handleBookmark = async () => {
    if (!requireAuth("bookmark")) return;
    if (!user) return;
    try {
      const created = await learning.toggleBookmark({
        book,
        chapter,
        verse: verse.verse,
        label: `${bookHe || book} · פרק ${numberToHebrewLetter(chapter)} · פסוק ${numberToHebrewLetter(verse.verse)}`,
      });
      setBookmarked(!!created);
      toast.success(created ? t("verse_overlay.toast_bookmark_added") : t("verse_overlay.toast_bookmark_removed"));
    } catch {
      toast.error(t("verse_overlay.toast_bookmark_error"));
    }
  };

  const handleSaveNote = async () => {
    if (!requireAuth("note")) return;
    if (!user) return;
    if (!noteText.trim()) {
      toast.error(t("verse_overlay.toast_note_empty"));
      return;
    }
    setSavingNote(true);
    try {
      await learning.upsertNote({
        book,
        chapter,
        verse: verse.verse,
        text: noteText,
      });
      toast.success(t("verse_overlay.toast_note_saved"));
    } catch {
      toast.error(t("verse_overlay.toast_note_save_error"));
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async () => {
    const live = learning.getNote(book, chapter, verse.verse);
    if (!live) return;
    try {
      await learning.removeNote(live.id);
      setNoteText("");
      toast.success(t("verse_overlay.toast_note_deleted"));
    } catch {
      toast.error(t("verse_overlay.toast_note_delete_error"));
    }
  };

  const handleShare = async () => {
    if (!verse) return;
    const text = `${stripNikud(verse.he)}\n— ${bookHe || book} ${numberToHebrewLetter(chapter)}:${numberToHebrewLetter(verse.verse)}`;
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t("verse_overlay.toast_copied"));
      }
    } catch {}
  };

  const handleDownloadImage = async () => {
    if (!verse || !shareCardRef.current) return;
    setExporting(true);
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(shareCardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: undefined,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `tanakh-${book}-${chapter}-${verse.verse}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(t("verse_overlay.toast_image_saved"));
    } catch (e) {
      console.error("share image error:", e);
      toast.error(t("verse_overlay.toast_image_create_error"));
    } finally {
      setExporting(false);
    }
  };

  const handleShareImage = async () => {
    if (!verse || !shareCardRef.current) return;
    setExporting(true);
    try {
      const { toBlob } = await import("html-to-image");
      const blob = await toBlob(shareCardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
      });
      if (!blob) throw new Error("no blob");
      const file = new File(
        [blob],
        `tanakh-${book}-${chapter}-${verse.verse}.png`,
        { type: "image/png" }
      );
      if (
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        navigator.share
      ) {
        await navigator.share({
          files: [file],
          title: `${bookHe || book} ${numberToHebrewLetter(chapter)}:${numberToHebrewLetter(verse.verse)}`,
          text: "תוריא · Torahya",
        });
      } else {
        // Fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(t("verse_overlay.toast_image_downloaded"));
      }
    } catch (e) {
      console.error("share image error:", e);
      toast.error(t("verse_overlay.toast_image_share_error"));
    } finally {
      setExporting(false);
    }
  };

  if (!verse) return null;
  const verseHe = nikud ? verse.he : stripNikud(verse.he);

  // Title header — renders with Sheet primitives inside a Sheet, or plain
  // HTML when we're an inline panel (Sheet primitives require Dialog ctx).
  const titleHeader =
    presentation === "panel" ? (
      <div className="text-right space-y-3 flex flex-col">
        <div className="flex items-center justify-between">
          <h2
            data-testid="verse-panel-title"
            className="font-heading text-2xl text-foreground"
          >
            {isEn
              ? `${(bookEn || bookHe) || book} · Chapter ${chapter} · Verse ${verse.verse}`
              : `${bookHe} · פרק ${numberToHebrewLetter(chapter)} · פסוק ${numberToHebrewLetter(verse.verse)}`}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="verse-panel-close"
            className="text-muted-foreground hover:text-gold"
            aria-label={t("verse_overlay.close_aria")}
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
        <div className="ornamental-divider">✦</div>
      </div>
    ) : (
      <SheetHeader className="text-right space-y-3 pe-10">
        <div className="flex items-center justify-between">
          <SheetTitle className="font-heading text-xl sm:text-2xl text-foreground leading-tight">
            {isEn
              ? `${(bookEn || bookHe) || book} · Chapter ${chapter} · Verse ${verse.verse}`
              : `${bookHe} · פרק ${numberToHebrewLetter(chapter)} · פסוק ${numberToHebrewLetter(verse.verse)}`}
          </SheetTitle>
        </div>
        <div className="ornamental-divider">✦</div>
      </SheetHeader>
    );

  // Shared body — same markup, same state, two wrappers.
  const body = (
    <>
      {titleHeader}

        <div className="mt-5 space-y-5">
          {/* Verse text — language-aware ordering: in EN mode, English is
              prominent and Hebrew is the secondary line; in HE mode,
              Hebrew is prominent. The Hebrew text always renders RTL
              regardless of UI language so the sacred text shape stays. */}
          {!isEn && (
            <>
              <div
                className="torah-verse hebrew-text text-2xl md:text-3xl text-foreground leading-loose"
                dir="rtl"
                data-testid="overlay-verse-text"
              >
                {verseHe}
              </div>
              {verse.en && (
                <p
                  dir="ltr"
                  lang="en"
                  className="font-latin italic text-sm text-muted-foreground text-left"
                >
                  {verse.en}
                </p>
              )}
            </>
          )}
          {isEn && (
            <>
              {verse.en ? (
                <p
                  dir="ltr"
                  lang="en"
                  className="font-latin text-xl md:text-2xl text-foreground leading-relaxed text-left"
                  data-testid="overlay-verse-text-en"
                >
                  {verse.en}
                </p>
              ) : (
                <p dir="ltr" className="text-xs text-muted-foreground italic">
                  English translation not available
                </p>
              )}
              <div
                className="torah-verse hebrew-text text-base md:text-lg text-muted-foreground leading-loose"
                dir="rtl"
              >
                {verseHe}
              </div>
            </>
          )}

          {/* Quick actions */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <div className="flex items-center gap-1.5">
              <Highlighter className="h-4 w-4 text-gold" strokeWidth={1.5} />
              {HL_COLORS.map((c) => (
                <button
                  key={c.key}
                  data-testid={`highlight-${c.key}`}
                  data-reason={c.reason}
                  onClick={() => handleHighlight(c.key, c.reason)}
                  className={`h-6 w-6 rounded-full transition-all ${c.cls} ${
                    effectiveHighlight && effectiveHighlight.color === c.key
                      ? "ring-2 ring-offset-2 ring-gold ring-offset-background scale-110"
                      : "opacity-75 hover:opacity-100 hover:scale-110"
                  }`}
                  aria-label={t("verse_overlay.highlight_label_aria", { name: t(c.nameKey) })}
                  title={t(c.nameKey)}
                />
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBookmark}
              data-testid="overlay-bookmark"
              className="sacred-btn-ghost !py-1.5"
            >
              <Bookmark className="h-4 w-4" strokeWidth={1.5} />
              {bookmarked ? t("verse_overlay.saved_label") : t("verse_overlay.bookmark_label")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              data-testid="overlay-share"
              className="sacred-btn-ghost !py-1.5"
            >
              <Share2 className="h-4 w-4" strokeWidth={1.5} />
              {t("verse_overlay.share_label")}
            </Button>
            <SaveToNotebookButton
              variant="chip"
              testIdSuffix="overlay"
              className="sacred-btn-ghost !py-1.5"
              payload={{
                source_type: "verse",
                source_book: book,
                source_chapter: chapter,
                source_verse: verse.verse,
                source_ref_he: `${bookHe} ${numberToHebrewLetter(chapter)}:${numberToHebrewLetter(verse.verse)}`,
                content_he: verseHe,
                content_text: verse.en || "",
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="overlay-share-image"
                  className="sacred-btn-ghost !py-1.5"
                  disabled={exporting}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <ImageIcon className="h-4 w-4" strokeWidth={1.5} />
                  )}
                  {t("verse_overlay.image_label")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" dir="rtl">
                <DropdownMenuItem
                  onClick={handleShareImage}
                  data-testid="share-image-share-menu"
                >
                  <Share2 className="ms-2 h-4 w-4" strokeWidth={1.5} />
                  {t("verse_overlay.share_image_label")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDownloadImage}
                  data-testid="share-image-download-menu"
                >
                  <ImageIcon className="ms-2 h-4 w-4" strokeWidth={1.5} />
                  {t("verse_overlay.download_image_label")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Torah lesson builder CTAs.
              Mobile: full-width stacked column (each button 100% wide, gap-3,
                      easier 44px touch targets, no overlapping wrap).
              md+   : original inline row with flex-wrap. */}
          <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:gap-2 pt-1">
            <Button
              onClick={() => {
                // Open the global Chavruta panel with this verse
                // pinned as context. Same UX path as the lesson
                // basket: tap a verse → menu → action.
                //
                // Anonymous visitors must sign in first — the
                // underlying /api/chavruta/ask endpoint is auth-gated
                // for quota / history, and without an explicit gate
                // here the user would tap, see no panel open (the
                // bubble returns null when user is null), and be
                // confused. requireAuth() pops the GlobalAuthGate
                // modal with the "beit_midrash" / chavruta copy.
                if (!requireAuth("beit_midrash")) return;
                const ref = `${book} ${chapter}:${verse.verse}`;
                onClose?.();  // dismiss this overlay so the chavruta
                              // panel has the full screen on mobile.
                openChavrutaWithContext({
                  ref,
                  selected_text: stripNikud(verse.he || ""),
                });
              }}
              data-testid="open-chavruta-on-verse"
              className="sacred-btn-gold w-full justify-center md:w-auto md:!py-1.5"
              size="sm"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
              {t("verse_overlay.learn_with_chavruta")}
            </Button>
            <Button
              onClick={gate(() => setShowLessonBuilder(true), { feature: "lesson" })}
              data-testid="open-lesson-builder"
              className="sacred-btn w-full justify-center md:w-auto md:!py-1.5"
              size="sm"
              variant="outline"
            >
              <GraduationCap className="h-4 w-4" strokeWidth={1.5} />
              {t("verse_overlay.build_lesson")}
            </Button>
            <Button
              onClick={gate(() => setShowWhoSaysWhat(true), { feature: "who_says_what" })}
              data-testid="open-who-says-what"
              className="sacred-btn w-full justify-center md:w-auto md:!py-1.5"
              size="sm"
              variant="outline"
            >
              <Users className="h-4 w-4" strokeWidth={1.5} />
              {t("verse_overlay.who_says_what")}
            </Button>
            {(() => {
              const probe = {
                type: "verse",
                book,
                chapter,
                verse: verse.verse,
              };
              const already = basket.has(probe);
              return (
                <Button
                  onClick={() => {
                    if (already) {
                      toast.message(t("verse_overlay.toast_already_in_basket"));
                      return;
                    }
                    basket.add({
                      ...probe,
                      he: verse.he,
                      en: verse.en,
                      book_he: bookHe,
                    });
                    toast.success(t("verse_overlay.toast_added_to_basket"));
                  }}
                  data-testid="add-verse-to-basket"
                  data-in-basket={already ? "true" : "false"}
                  className="sacred-btn w-full justify-center md:w-auto md:!py-1.5"
                  size="sm"
                  variant="outline"
                >
                  {already ? (
                    <Check className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  ) : (
                    <ShoppingBasket className="h-4 w-4" strokeWidth={1.5} />
                  )}
                  {already ? t("verse_overlay.in_basket_short") : t("verse_overlay.add_to_basket")}
                </Button>
              );
            })()}
          </div>

          <div className="gold-rule" />

          {/* Simplified Tabs - AI Commentary, Classic Commentaries, Notes */}
          <Tabs value={tab} onValueChange={setTab} className="w-full" dir="rtl">
            <TabsList className="w-full justify-start bg-transparent border border-border/60 rounded-sm p-1 gap-2">
              <TabsTrigger
                value="ai"
                data-testid="tab-ai"
                className="data-[state=active]:bg-gold/15 data-[state=active]:text-foreground font-heading"
              >
                <Sparkles className="h-4 w-4 ms-2" strokeWidth={1.5} />
                {t("verse_overlay.smart_commentary")}
              </TabsTrigger>
              <TabsTrigger
                value="commentary"
                data-testid="tab-commentary"
                className="data-[state=active]:bg-gold/15 data-[state=active]:text-foreground font-heading"
              >
                <BookOpenCheck className="h-4 w-4 ms-2" strokeWidth={1.5} />
                {t("verse_overlay.classical_commentary")}
              </TabsTrigger>
              <TabsTrigger
                value="notes"
                data-testid="tab-notes"
                className="data-[state=active]:bg-gold/15 data-[state=active]:text-foreground font-heading"
              >
                <StickyNote className="h-4 w-4 ms-2" strokeWidth={1.5} />
                {t("verse_overlay.my_notes")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ai" className="mt-5">
              <AIAssistant
                book={book}
                chapter={chapter}
                verse={verse.verse}
                enabled={!!user}
              />
            </TabsContent>

            <TabsContent value="commentary" className="mt-5">
              {loadingComm ? (
                <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin text-gold" strokeWidth={1.5} />
                  <span className="font-heading text-sm">{t("verse_overlay.loading_commentators")}</span>
                </div>
              ) : commentaries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  {t("verse_overlay.no_commentary")}
                </div>
              ) : (
                <div className="space-y-2">
                  {commentaries.map((comm) => (
                    <CommentaryCard
                      key={comm.commentator}
                      commentator={comm.commentator}
                      commentatorHe={comm.commentator_he}
                      textHe={comm.he}
                      textEn={comm.en}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="notes" className="mt-5 space-y-3">
              {!user && (
                <p className="text-sm text-muted-foreground">
                  {t("verse_overlay.must_sign_in_to_write")}
                </p>
              )}
              <Textarea
                data-testid="note-textarea"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={t("verse_overlay.note_placeholder")}
                className="min-h-[160px] bg-card border-border/60 focus-visible:ring-gold font-body text-right"
                disabled={!user}
                dir="rtl"
              />
              <div className="flex items-center justify-between">
                {effectiveNote && (
                  <Button
                    variant="ghost"
                    onClick={handleDeleteNote}
                    data-testid="delete-note-btn"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 ms-2" strokeWidth={1.5} />
                    {t("verse_overlay.delete")}
                  </Button>
                )}
                <Button
                  onClick={handleSaveNote}
                  disabled={savingNote || !user}
                  data-testid="save-note-btn"
                  className="sacred-btn ms-auto"
                >
                  <Save className="h-4 w-4" strokeWidth={1.5} />
                  {effectiveNote ? t("verse_overlay.update") : t("verse_overlay.save")}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
    </>
  );

  // Render-phase: mobile sheet vs desktop inline panel.
  const shareTarget = (
    <div
      aria-hidden
      style={{
        position: "fixed",
        left: -99999,
        top: 0,
        pointerEvents: "none",
        opacity: 0,
      }}
    >
      <ShareVerseCard
        ref={shareCardRef}
        verseHe={verseHe}
        verseEn={verse.en}
        bookHe={bookHe || book}
        bookEn={bookEn || book}
        chapter={chapter}
        verse={verse.verse}
        theme={theme}
      />
    </div>
  );

  const lessonDialogs = (
    <>
      <LessonBuilder
        open={showLessonBuilder}
        onClose={() => setShowLessonBuilder(false)}
        book={book}
        bookHe={bookHe}
        verses={[{ chapter, verse: verse.verse }]}
      />
      <WhoSaysWhat
        open={showWhoSaysWhat}
        onClose={() => setShowWhoSaysWhat(false)}
        book={book}
        bookHe={bookHe}
        chapter={chapter}
        verse={verse.verse}
        commentators={["Rashi", "Ramban"]}
        onBuildLesson={gate(() => setShowLessonBuilder(true), { feature: "lesson" })}
      />
      {RequireAuthModal}
    </>
  );

  if (presentation === "panel") {
    // Desktop: render inline inside a resizable right panel.
    if (!open) return null;
    return (
      <>
        <div
          dir="rtl"
          data-testid="verse-panel"
          className="h-full w-full overflow-y-auto bg-background p-6 md:p-8 pb-24"
          style={{
            // Defensive scroll-chain isolation: same pattern used to
            // tame the nested-scroll trap inside "Pour Out Your Heart".
            // `contain` prevents the page-level vertical scroll from
            // ever interfering with the panel's own scrolling; the
            // `-webkit-overflow-scrolling: touch` keeps iOS momentum
            // smooth when the panel ends up on an iPad in landscape.
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {body}
        </div>
        {shareTarget}
        {lessonDialogs}
      </>
    );
  }

  // Mobile/tablet: keep the existing Sheet presentation.
  // Mobile: present as a bottom sheet (App Store native feel). Desktop:
  // keep the right-edge slide. (Hooks moved to component top to keep
  // ordering stable across early returns above.)
  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent
          side={isMobile ? "bottom" : "left"}
          dir="rtl"
          className={
            isMobile
              ? "w-full max-h-[88vh] overflow-y-auto bg-background border-t border-gold/30 rounded-t-xl pb-[calc(env(safe-area-inset-bottom)+5rem)]"
              : "w-full sm:max-w-xl md:max-w-2xl overflow-y-auto bg-background border-l border-gold/30 pb-24"
          }
          style={{
            // Match the panel-mode scroll guarantees so the bottom
            // commentators (often Tosafot Yom Tov / Targum Jonathan
            // / Metzudot — the long ones) are always reachable.
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
          data-testid="verse-overlay"
          data-mobile={isMobile ? "true" : "false"}
        >
          {body}
        </SheetContent>
      </Sheet>
      {shareTarget}
      {lessonDialogs}
    </>
  );
}
